import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import apiManager from '../../../../lib/api';

interface GoogleReview {
  _id: string;
  placeId: string;
  locationName: string;
  authorName: string;
  authorUrl: string;
  profilePhotoUrl: string;
  rating: number;
  text: string;
  relativeTimeDescription: string;
  time: number;
}

const TestimonialsSection = () => {
  const [reviews, setReviews] = useState<GoogleReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const autoPlayRef = useRef<number | null>(null);

  // Cargar reseñas desde la API
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await apiManager.client.get<GoogleReview[]>('/google-reviews?limit=10&minRating=4');
        if (response.success && response.data && Array.isArray(response.data)) {
          setReviews(response.data);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Manejar la reproducción automática
  useEffect(() => {
    if (isAutoPlaying && reviews.length > 0) {
      autoPlayRef.current = window.setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % reviews.length);
      }, 5000);
    }

    return () => {
      if (autoPlayRef.current !== null) {
        window.clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, reviews]);

  // Pausar la reproducción automática al interactuar
  const handleManualNavigation = (index: number) => {
    if (reviews.length === 0) return;
    setActiveIndex(index);
    setIsAutoPlaying(false);

    // Reanudar después de un tiempo
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 8000);
  };

  // Renderizar estrellas para calificación
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <svg
        key={i}
        xmlns="http://www.w3.org/2000/svg"
        className={`h-5 w-5 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`}
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  if (loading) {
    return (
      <div className="relative">
        {/* Skeleton Loader */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 animate-pulse">
          <div className="md:col-span-2 bg-white rounded-xl shadow-lg p-6 sm:p-8 min-h-[300px]">
            <div className="flex items-center mb-4">
              <div className="h-14 w-14 rounded-full bg-gray-200 mr-4"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              </div>
            </div>
            <div className="space-y-3 mb-8">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
            <div className="h-4 bg-gray-200 rounded w-40 mt-auto"></div>
          </div>

          <div className="hidden md:flex flex-col space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-md p-5 h-32">
                <div className="flex items-center mb-3">
                  <div className="h-10 w-10 rounded-full bg-gray-200 mr-3"></div>
                  <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                  </div>
                </div>
                <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-4/5"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (reviews.length === 0) return null;

  return (
    <div className="relative">
      {/* Indicadores de navegación */}
      <div className="absolute top-1/2 left-0 right-0 z-10 hidden md:flex justify-between items-center px-4 -translate-y-1/2">
        <button
          onClick={() => handleManualNavigation((activeIndex - 1 + reviews.length) % reviews.length)}
          className="p-2 rounded-full bg-white/80 shadow-md hover:bg-white transition-colors"
          aria-label="Testimonio anterior"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          onClick={() => handleManualNavigation((activeIndex + 1) % reviews.length)}
          className="p-2 rounded-full bg-white/80 shadow-md hover:bg-white transition-colors"
          aria-label="Testimonio siguiente"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Carrusel de testimonios */}
      <div className="relative overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={`testimonial-${activeIndex}`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="grid md:grid-cols-3 gap-6 md:gap-8"
          >
            {/* Testimonio destacado */}
            <div
              className="md:col-span-2 bg-white rounded-xl shadow-lg p-6 sm:p-8 flex flex-col justify-between min-h-[300px] cursor-pointer hover:shadow-xl transition-shadow relative group"
              onClick={() => window.open(`https://www.google.com/maps/place/?q=place_id:${reviews[activeIndex].placeId}`, '_blank')}
            >
              <div className="absolute top-4 right-4 text-gray-400 group-hover:text-blue-500 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </div>

              {reviews[activeIndex].text ? (
                // Diseño con texto
                <div>
                  <div className="flex items-center mb-4">
                    <div className="h-14 w-14 rounded-full overflow-hidden mr-4 border-2 border-gray-100 bg-gray-200 shrink-0">
                      <img
                        src={reviews[activeIndex].profilePhotoUrl}
                        alt={reviews[activeIndex].authorName}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${reviews[activeIndex].authorName}&background=random`;
                        }}
                      />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 line-clamp-1">{reviews[activeIndex].authorName}</h3>
                      <div className="text-sm text-gray-500 font-medium">{reviews[activeIndex].locationName}</div>
                      <div className="flex mt-1 items-center space-x-2">
                        <div className="flex">
                          {renderStars(reviews[activeIndex].rating)}
                        </div>
                        <span className="text-xs text-gray-400">{reviews[activeIndex].relativeTimeDescription}</span>
                      </div>
                    </div>
                  </div>

                  <blockquote className="text-gray-700 text-lg leading-relaxed mb-4 line-clamp-4 italic">
                    "{reviews[activeIndex].text}"
                  </blockquote>
                </div>
              ) : (
                // Diseño SIN texto (centrado)
                <div className="flex flex-col items-center justify-center flex-1 py-8 text-center">
                  <div className="h-20 w-20 rounded-full overflow-hidden mb-4 border-4 border-blue-50 bg-gray-200">
                    <img
                      src={reviews[activeIndex].profilePhotoUrl}
                      alt={reviews[activeIndex].authorName}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = `https://ui-avatars.com/api/?name=${reviews[activeIndex].authorName}&background=random`;
                      }}
                    />
                  </div>
                  <div className="flex mb-3 transform scale-125">
                    {renderStars(reviews[activeIndex].rating)}
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 mb-1">{reviews[activeIndex].authorName}</h3>
                  <div className="text-gray-500 font-medium">{reviews[activeIndex].locationName}</div>
                  <div className="text-sm text-gray-400 mt-2">{reviews[activeIndex].relativeTimeDescription}</div>
                </div>
              )}

              <div className="flex items-center text-sm text-gray-500 mt-4 pt-4 border-t border-gray-100">
                <img src="/images/google-g-logo.svg" alt="Google" className="h-5 w-5 mr-2" />
                <span className="font-medium">Reseña verificada de Google</span>
              </div>
            </div>

            {/* Testimonios relacionados (Siguientes en la cola) */}
            <div className="hidden md:flex flex-col space-y-4">
              {[
                (activeIndex + 1) % reviews.length,
                (activeIndex + 2) % reviews.length
              ].map((index, displayIndex) => {
                const review = reviews[index];
                return (
                  <div
                    key={review._id || index} // Fallback key si _id no es único o presente
                    className={`bg-white rounded-xl shadow-md p-5 cursor-pointer transition-all hover:shadow-lg ${displayIndex === 0 ? 'transform -rotate-1' : 'transform rotate-1'
                      }`}
                    onClick={() => handleManualNavigation(index)}
                  >
                    <div className="flex items-center mb-3">
                      <div className="h-10 w-10 rounded-full overflow-hidden mr-3 border-2 border-gray-100 bg-gray-200 shrink-0">
                        <img
                          src={review.profilePhotoUrl}
                          alt={review.authorName}
                          className="h-full w-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = `https://ui-avatars.com/api/?name=${review.authorName}&background=random`;
                          }}
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm text-gray-900 truncate max-w-[120px]">{review.authorName}</h3>
                        <div className="flex">
                          {renderStars(review.rating)}
                        </div>
                      </div>
                    </div>
                    {review.text ? (
                      <blockquote className="text-gray-600 text-sm line-clamp-3">
                        "{review.text}"
                      </blockquote>
                    ) : (
                      <div className="text-gray-400 text-xs italic mt-2">
                        Sin comentario escrito
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Indicadores de puntos para móviles */}
      <div className="flex justify-center mt-6 md:hidden">
        {reviews.map((_, index) => (
          <button
            key={index}
            onClick={() => handleManualNavigation(index)}
            className={`h-2 w-2 mx-1 rounded-full transition-colors ${index === activeIndex ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            aria-label={`Ir al testimonio ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialsSection;